import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Text, Card, Drawer } from "@pushpress/shared-components";
import SetupItem from "./SetupItem";
import SetupRulesPane from "./SetupRulesPane";
import SetupNamePane from "./SetupNamePane";
import { useAppointmentTypesQuery } from "../../apis/appointmentApi.hooks";
import { useGetCalendarClassTypesSelectQuery } from "../../components/controllers/CalendarItemTypeSelect/CalendarItemTypeSelect-generated.hooks";
import { sortByName } from "../../utils/sortUtils";
import SetupMonthPane from "./SetupMonthPane";
import { useNameSuggestionsQuery } from "./committedClubApi.hooks";
import {
  BadgesService,
  BadgeTemplate,
} from "@pushpress/committed-club-typescript-sdk";
import CommitedClubAboutPanel from "./CommitedClubAboutPanel";
import {
  appointmentCheckinOptions,
  classCheckinOptions,
  eventCheckinOptions,
  getMonthOptionFromDateString,
  getOptionFromMonthIndex,
  getStartDateFromMonthOption,
  showErrorToast,
} from "./utils";
import { ClubCheckinRules, MonthOption, UpdateDataBody } from "./types";
import { CommittedClubEvent } from "./CommittedClubEvents";
import { useAnalytics } from "../../components/providers/AnalyticsProvider";
import { DrawerPanel } from "../../components/common/DrawerPanel";
import { Helmet } from "react-helmet";

type SetupView = undefined | "name" | "rules" | "start_month";

type CommittedClubPreLaunchProps = {
  badgeTemplate?: BadgeTemplate | undefined;
  refetchBadgeTemplate: () => void;
};

const CommittedClubPreLaunch = (props: CommittedClubPreLaunchProps) => {
  const [isSetup, setIsSetup] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState<string>();
  const [rules, setRules] = useState<ClubCheckinRules>();
  const [sheetView, setSheetView] = useState<SetupView>();
  const [startMonth, setStartMonth] = useState<MonthOption>();
  const [hasExistingClub, setHasExistingClub] = useState<boolean>();
  const [badgeTemplate, setBadgeTemplate] = useState<BadgeTemplate>();

  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (!props.badgeTemplate) {
      return;
    }

    async function seedStateFromBadgeTemplate() {
      const clubDetails = props.badgeTemplate;

      setBadgeTemplate(clubDetails);

      setIsSetup(true);
      setHasExistingClub(true);
      setName(clubDetails.title);

      const monthNumber = parseInt(clubDetails.startTime.split("-")[1], 10);
      const monthOption = getOptionFromMonthIndex(monthNumber - 1); // zero-indexed
      setStartMonth(monthOption);

      const {
        appointmentSelectionStrategy,
        classSelectionStrategy,
        includeEvents,
        includeOpenGym,
        selectedAppointmentTypeIds,
        selectedClassIds,
      } = clubDetails.criteria;

      const eventTypes =
        includeEvents && includeOpenGym
          ? "BOTH"
          : includeEvents
          ? "EVENTS"
          : includeOpenGym
          ? "OPENGYMS"
          : "NONE";

      setRules({
        checkinCount: clubDetails.criteriaTotal,
        checkinTypes: selectedClassIds,
        appointmentTypes: selectedAppointmentTypeIds,
        checkinTypeLevel: classCheckinOptions.find(
          (o) => o.value === classSelectionStrategy,
        ),
        appointmentTypeLevel: appointmentCheckinOptions.find(
          (o) => o.value === appointmentSelectionStrategy,
        ),
        eventTypes: eventCheckinOptions.find((o) => o.value === eventTypes),
      });
    }

    seedStateFromBadgeTemplate();
  }, [props.badgeTemplate]);

  const aiNameSuggestions = useNameSuggestionsQuery();

  const { data: classTypes } = useGetCalendarClassTypesSelectQuery({
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  const classTypesFiltered = useMemo(() => {
    const calendarTypes = classTypes?.getCalendarClassTypes || [];

    const classTypeOptions = calendarTypes
      .filter(
        (calendarType) => calendarType.isActive && !calendarType.isDeleted,
      )
      .sort(sortByName)
      .map((calendarType) => ({
        label: calendarType.name,
        value: calendarType.uuid,
      }));

    return classTypeOptions;
  }, [classTypes]);

  const { data: appointmentTypesResponse } = useAppointmentTypesQuery();

  const appointmentTypesFiltered = useMemo(() => {
    const appointmentTypes = appointmentTypesResponse?.data ?? [];

    const appointmentTypeOptions = appointmentTypes
      .sort(sortByName)
      .map((appointmentType) => ({
        label: appointmentType.name,
        value: appointmentType.uuid,
      }));

    return appointmentTypeOptions;
  }, [appointmentTypesResponse]);

  const closeDrawer = useCallback(
    (trackClose = true) => {
      if (trackClose) {
        trackEvent(CommittedClubEvent.Closed, {
          state: "pre_launch",
          app: "committed_club",
          page: `${sheetView}_panel`,
        });
      }
      setSheetView(undefined);
    },
    [sheetView, trackEvent],
  );

  const createClub = useCallback(
    async (month: MonthOption) => {
      setSubmitting(true);
      closeDrawer(false);

      const response = await BadgesService.create({
        body: {
          title: name,
          criteriaTotal: rules.checkinCount,
          startDate: getStartDateFromMonthOption(month),
          criteria: {
            classSelectionStrategy: rules.checkinTypeLevel.value,
            appointmentSelectionStrategy: rules.appointmentTypeLevel.value,
            includeEvents: ["BOTH", "EVENTS"].includes(rules.eventTypes.value),
            includeOpenGym: ["BOTH", "OPENGYMS"].includes(
              rules.eventTypes.value,
            ),
            selectedClassIds:
              rules.checkinTypeLevel.value === "SPECIFIC"
                ? rules.checkinTypes
                : null,
            selectedAppointmentTypeIds:
              rules.appointmentTypeLevel.value === "SPECIFIC"
                ? rules.appointmentTypes
                : null,
          },
        },
      });

      if (response.error) {
        closeDrawer();
        setSubmitting(false);
        showErrorToast(response.error.error);
        return;
      }

      const newBadgeTemplate = response.data;

      if (new Date() >= new Date(newBadgeTemplate.startTime)) {
        props.refetchBadgeTemplate();
      } else {
        setBadgeTemplate(newBadgeTemplate);
        setIsSetup(true);
        setStartMonth(month);
        closeDrawer();
        setSubmitting(false);
      }
    },
    [name, rules, props, closeDrawer, setBadgeTemplate],
  );

  const updateClub = useCallback(
    async (update: Partial<UpdateDataBody>) => {
      const response = await BadgesService.update({
        body: { ...badgeTemplate!, ...update },
      });

      if (response.error) {
        showErrorToast(response.error.toString());
        return;
      }

      const updatedBadgeTemplate = response.data;

      if (new Date() >= new Date(updatedBadgeTemplate.startTime)) {
        props.refetchBadgeTemplate();
      } else {
        setBadgeTemplate(updatedBadgeTemplate);
        if (update.startDate) {
          setStartMonth(getMonthOptionFromDateString(update.startDate));
        }
        closeDrawer(false);
      }
    },
    [badgeTemplate, props, closeDrawer],
  );

  const showNamePane = useCallback(() => {
    trackEvent(CommittedClubEvent.NameSelected, {
      state: "pre_launch",
      app: "committed_club",
      page: "committed_club_dashboard",
    });
    setSheetView("name");
  }, [trackEvent]);

  const showRulesPane = useCallback(() => {
    trackEvent(CommittedClubEvent.RulesSelected, {
      state: "pre_launch",
      app: "committed_club",
      page: "committed_club_dashboard",
    });
    setSheetView("rules");
  }, [trackEvent]);

  const showMonthPane = useCallback(() => {
    trackEvent(CommittedClubEvent.StartMonthSelected, {
      state: "pre_launch",
      app: "committed_club",
      page: "committed_club_dashboard",
    });
    setSheetView("start_month");
  }, [trackEvent]);

  const drawerTitle =
    sheetView === "name"
      ? "Choose a name"
      : sheetView === "rules"
      ? "Configure your rules"
      : sheetView === "start_month"
      ? "Set the start month"
      : "";

  const drawer = (
    <Drawer visible={!!sheetView} anchor="right" width="400px">
      <DrawerPanel title={drawerTitle} onClose={closeDrawer}>
        <div className="tw-px-4 md:tw-px-0">
          {sheetView === "name" && (
            <SetupNamePane
              value={name}
              setName={setName}
              isClubSetup={isSetup}
              hasLaunched={false}
              suggestionsHook={aiNameSuggestions}
              updateClub={updateClub}
              closeDrawer={closeDrawer}
            />
          )}

          {sheetView === "rules" && (
            <SetupRulesPane
              value={rules}
              hasLaunched={false}
              isClubSetup={isSetup}
              classTypes={classTypesFiltered}
              appointmentTypes={appointmentTypesFiltered}
              setRules={setRules}
              updateClub={updateClub}
              closeDrawer={closeDrawer}
            />
          )}

          {sheetView === "start_month" && (
            <SetupMonthPane
              value={startMonth}
              isClubSetup={isSetup}
              hasLaunched={false}
              ready={!!(name && rules)}
              hasExistingClub={hasExistingClub}
              setHasExistingClub={setHasExistingClub}
              createClub={createClub}
              updateClub={updateClub}
            />
          )}
        </div>
      </DrawerPanel>
    </Drawer>
  );

  const content = (
    <Grid item xs={12} md={8} alignItems="flex-start">
      <Card>
        <Card.Content>
          <div className="tw-py-4">
            <Text weight="600" variant="heading-4" color="black">
              {isSetup ? "Your Committed Club" : "Set Up Your Committed Club"}
            </Text>

            <div className="tw-h-6" />

            <SetupItem
              title="Name"
              instructionTitle="Name your Committed Club"
              value={[name]}
              completed={!!name}
              buttonText="Choose a name"
              stepNumber={1}
              onClick={showNamePane}
            />

            <div className="tw-h-4" />

            <SetupItem
              title="Rules"
              instructionTitle="Set rules for your Committed Club"
              value={[
                `${rules?.checkinCount} check-in${
                  rules?.checkinCount == 1 ? "" : "s"
                } / Month`,
                `Counting ${rules?.checkinTypeLevel.overview}, ${rules?.appointmentTypeLevel.overview}, ${rules?.eventTypes.overview}`,
              ]}
              buttonText="Choose rules"
              completed={!!rules}
              stepNumber={2}
              onClick={showRulesPane}
            />

            <div className="tw-h-4" />

            <SetupItem
              title="Start Month"
              instructionTitle="Select a start month"
              value={[startMonth?.label]}
              completed={!!startMonth}
              buttonText="Choose when to start"
              loading={submitting}
              stepNumber={3}
              onClick={showMonthPane}
            />
          </div>
        </Card.Content>
      </Card>
    </Grid>
  );

  const completedBanner = isSetup && (
    <div className="tw-bg-green-100 tw-border tw-border-green-300 tw-rounded-lg tw-px-6 tw-py-4 tw-text-green-800 tw-shadow-sm">
      {`You’re all set up. On ${startMonth?.label} 1st, “${name}” will start showing in your Members App and you will be able to view your members’ progress.`}
    </div>
  );

  return (
    <>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Helmet>
      {drawer}
      <div className="tw-h-1" />
      {completedBanner}
      <Grid
        container
        justifyContent="space-between"
        spacing={4}
        columnSpacing={4}
      >
        <CommitedClubAboutPanel hasLaunched={false} isClubSetup={isSetup} />
        {content}
      </Grid>
      <div className="tw-h-4" />
    </>
  );
};

export default CommittedClubPreLaunch;
